import { useCallback, useEffect, useRef } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import bigWinTitle from '../../../../../../assets/big-win-title.png';
import jackpotTitle from '../../../../../../assets/jackpot-title.png';
import { analytics } from '../../../../../../services/analytics';
import { useStores } from '../../../../../../stores/useStores';
import {
  useCanOpenSlotPopup,
  useOpenCrownJackpotWin,
  useOpenSlotPopup,
} from '../../../../../slotPopup/useOpenSlotPopup';
import { MAX_FRAME_SECONDS, SCROLL_SPEED_PX_PER_SEC, SNAP_PROBABILITY, SNAP_SPEED_MULTIPLIER } from '../../constants';
import { CrownJackpotTile } from '../../CrownJackpotTile/CrownJackpotTile';
import type { TileGeometry } from '../../hooks/useTileGeometry';
import { useWinnerSprites } from '../../hooks/useWinnerSprites';
import type { WinnerTileVM } from '../../types';
import { WinnerAnimatedTile } from '../../WinnerAnimatedTile/WinnerAnimatedTile';
import { WinnerTile } from '../WinnerTile/WinnerTile';

import styles from './WinnerConveyor.module.scss';

interface WinnerConveyorProps {
  tiles: WinnerTileVM[];
  // Viewport width, measured by the parent banner (drives re-measure on resize).
  containerWidth: number;
  geometry: TileGeometry;
  // The clipping viewport, used by special tiles to detect full visibility.
  viewportRef: React.RefObject<HTMLDivElement>;
}

interface EnteringTile {
  enteringTileIdx: number | null;
  // How far it has entered: 0 = left edge touches the viewport edge, tileWidth
  // = fully in view.
  phase: number;
}

// Pure geometry: from the wrapped scroll offset, work out which tile is entering
// and how far in it is. The belt scrolls right, so tiles enter from the left in
// order N-1, N-2, ..., 0. Exactly one tile is entering at a time, except during
// the small gap between tiles (enteringTileIdx === null).
export function getEnteringTile(offset: number, slotWidth: number, tileWidth: number, tileCount: number): EnteringTile {
  const k = Math.ceil(offset / slotWidth);
  const screenLeft = offset - k * slotWidth; // tile's left edge vs viewport edge, in (-slotWidth, 0]
  if (k >= 1 && k <= tileCount && screenLeft > -tileWidth && screenLeft < 0) {
    return { enteringTileIdx: tileCount - k, phase: screenLeft + tileWidth };
  }
  return { enteringTileIdx: null, phase: 0 };
}

// Belt position for a given monotonic scroll distance. Two identical copies of the
// tiles are rendered side by side ([copy A][copy B], each `cycleWidth` wide). The
// intro (first cycle) starts both copies fully off-screen left so the viewport begins
// empty over copy B and fills in; afterwards the translate wraps within [-cycleWidth, 0)
// — and because the copies are identical, that wrap is invisible, giving a seamless loop.
export function getBeltTranslate(distance: number, cycleWidth: number): number {
  if (cycleWidth <= 0) return 0;
  if (distance < cycleWidth) return distance - 2 * cycleWidth; // intro: empty → fills in
  return (distance % cycleWidth) - cycleWidth; // loop: seamless wrap
}

type WinnerTileType = 'crown_jackpot' | 'bingo' | 'jackpot' | 'big_win' | 'regular';

// The tile type reported on click. The data team's precedence (bingo before jackpot) differs from the
// render order below; the two only disagree for a win carrying both flags.
export function getTileType(tile: WinnerTileVM): WinnerTileType {
  switch (true) {
    case tile.crownJackpotLevel !== null:
      return 'crown_jackpot';
    case tile.isBingo:
      return 'bingo';
    case tile.isJackpot:
      return 'jackpot';
    case tile.isBigWin:
      return 'big_win';
    default:
      return 'regular';
  }
}

export const WinnerConveyor = observer(({ tiles, containerWidth, geometry, viewportRef }: WinnerConveyorProps) => {
  const { t } = useTranslation();
  const { authStore } = useStores();
  const { scSprite, ccSprite, bigWinSprite } = useWinnerSprites();
  const openSlotPopup = useOpenSlotPopup();
  const canOpenSlotPopup = useCanOpenSlotPopup();
  const openCrownJackpotWin = useOpenCrownJackpotWin();
  const navigate = useNavigate();
  const trackRef = useRef<HTMLDivElement>(null);

  // winners_banner.golden.md: one `tile` click for every tile kind, the kind riding tile_type.
  const sendTileClick = useCallback((tile: WinnerTileVM) => {
    void analytics.sendEvent('tile', {
      event_parent: 'lobby',
      event_category: 'winners_banner',
      event_action: 'click',
      event_type: 'tile_type',
      event_value: getTileType(tile),
      ...(tile.gameId ? { game_id: tile.gameId } : {}),
      page_url: {},
    });
  }, []);

  // What a tile does when clicked, or undefined when it isn't clickable for this viewer.
  const getTileAction = useCallback(
    (tile: WinnerTileVM): (() => void) | undefined => {
      const { crownJackpotLevel } = tile;
      if (crownJackpotLevel) {
        return (
          openCrownJackpotWin &&
          (() =>
            openCrownJackpotWin({
              gameId: tile.gameId,
              level: crownJackpotLevel,
              amount: tile.amount,
              mode: tile.mode,
            }))
        );
      }
      // Bingo tiles route into the bingo section (which requires a session).
      if (tile.isBingo) return authStore.authorized ? () => navigate('/bingo') : undefined;
      if (canOpenSlotPopup(tile.gameId)) return () => openSlotPopup(tile.gameId, 'winners_banner');
      return undefined;
    },
    [openCrownJackpotWin, authStore.authorized, navigate, canOpenSlotPopup, openSlotPopup],
  );

  const getTileSelectHandler = useCallback(
    (tile: WinnerTileVM) => {
      const action = getTileAction(tile);
      if (!action) return undefined;
      return () => {
        sendTileClick(tile);
        action();
      };
    },
    [getTileAction, sendTileClick],
  );

  useEffect(() => {
    const track = trackRef.current;
    if (!track || tiles.length === 0 || containerWidth === 0) {
      return undefined;
    }

    const tileCount = tiles.length;
    const slotWidth = geometry.stride;
    const tileWidth = geometry.tileWidth;
    // The track holds two copies; the loop wraps on one copy's stride. Tile size is driven
    // by these constants (passed to the tiles as CSS vars), so the geometry is exact and
    // doesn't need to measure the DOM. An imprecise cycleWidth would make the wrap jump.
    const cycleWidth = tileCount * slotWidth;

    // Pick which tiles trigger a burst — chosen once, to achieve 'new winner' effect.
    const snapTiles = new Set<number>();
    let prevSnapped = false;
    for (let i = 1; i < tileCount; i++) {
      const snapped: boolean = !prevSnapped && Math.random() < SNAP_PROBABILITY;
      if (snapped) snapTiles.add(i);
      prevSnapped = snapped;
    }

    // When a snap tile reaches its halfway point, the belt bursts forward by this
    // much — enough to bring the incoming tile (the snap's index - 1) fully into
    // view — while that incoming tile scales 0 -> 1 over the same advance.
    const burstAdvance = slotWidth + tileWidth / 2;

    let raf = 0;
    let lastTs = 0;
    const prefillTiles = Math.min(tileCount, Math.ceil(containerWidth / slotWidth));
    let distance = slotWidth * prefillTiles; // monotonic px scrolled

    // Active burst, or null. burstFrom is the distance the burst started at; the
    // incoming tile scales by progress through [burstFrom, burstFrom + burstAdvance].
    let burst: { from: number; incoming: HTMLElement | null } | null = null;

    const endBurst = () => {
      if (burst?.incoming) burst.incoming.style.transform = '';
      burst = null;
    };

    const frame = (ts: number) => {
      if (lastTs === 0) lastTs = ts;
      const dt = Math.min((ts - lastTs) / 1000, MAX_FRAME_SECONDS); // seconds since the last frame
      lastTs = ts;

      const offset = distance % cycleWidth;
      const { enteringTileIdx, phase } = getEnteringTile(offset, slotWidth, tileWidth, tileCount);

      // Snaps fire only during the intro (first cycle) — afterwards the belt loops calmly.
      // The visible copy during the intro is copy B (children [tileCount, 2*tileCount)),
      // so the incoming tile to scale is its copy-B node.
      const inIntro = distance < cycleWidth;
      if (inIntro && !burst && enteringTileIdx !== null && snapTiles.has(enteringTileIdx) && phase >= tileWidth / 2) {
        burst = { from: distance, incoming: track.children[tileCount + enteringTileIdx - 1] as HTMLElement | null };
      }

      // During a burst: move at burst speed and grow the incoming tile 0 -> 1,
      // both locked to the same progress, then settle back once the advance ends.
      let speed = SCROLL_SPEED_PX_PER_SEC;
      if (burst) {
        const progress = (distance - burst.from) / burstAdvance;
        if (progress >= 1) {
          endBurst();
        } else {
          speed *= SNAP_SPEED_MULTIPLIER;
          if (burst.incoming) burst.incoming.style.transform = `scale(${progress})`;
        }
      }

      track.style.transform = `translate3d(${getBeltTranslate(distance, cycleWidth)}px, 0, 0)`;
      distance += speed * dt;
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      endBurst();
    };
    // geometry deps re-init the belt when crossing the mobile/desktop breakpoint.
  }, [tiles.length, containerWidth, geometry.stride, geometry.tileWidth]);

  return (
    <div
      ref={trackRef}
      className={styles.track}
      style={
        {
          '--winner-tile-gap': `${geometry.tileGap}px`,
          '--winner-tile-width': `${geometry.tileWidth}px`,
        } as React.CSSProperties
      }
    >
      {/* Two identical copies form the seamless belt. Copy A (0) stays settled; copy B
          (1) is the one on screen during the intro, so it's the one that bursts. */}
      {[0, 1].flatMap((copy) =>
        tiles.map((tile) => {
          const key = `${copy}-${tile.winId}`;
          const onSelect = getTileSelectHandler(tile);
          switch (true) {
            case tile.crownJackpotLevel !== null:
              return (
                <CrownJackpotTile
                  key={key}
                  geometry={geometry}
                  playIntro={copy === 1}
                  sprite={tile.mode === 'SC' ? scSprite : ccSprite}
                  tile={tile}
                  viewportRef={viewportRef}
                  onSelect={onSelect}
                />
              );
            case tile.isJackpot:
              return (
                <WinnerAnimatedTile
                  key={key}
                  amountMaxFont={geometry.amountMaxFont}
                  amountMaxWidth={geometry.amountMaxWidth}
                  playBurst={copy === 1}
                  sprite={tile.mode === 'SC' ? scSprite : ccSprite}
                  tile={tile}
                  titleAlt={t('winnersBanner.jackpot')}
                  titleSrc={jackpotTitle}
                  variant="jackpot"
                  viewportRef={viewportRef}
                  onSelect={onSelect}
                />
              );
            case tile.isBigWin:
              return (
                <WinnerAnimatedTile
                  key={key}
                  amountMaxFont={geometry.amountMaxFont}
                  amountMaxWidth={geometry.amountMaxWidth}
                  playBurst={copy === 1}
                  sprite={bigWinSprite}
                  tile={tile}
                  titleAlt={t('winnersBanner.bigWin')}
                  titleSrc={bigWinTitle}
                  variant="bigWin"
                  viewportRef={viewportRef}
                  onSelect={onSelect}
                />
              );
            default:
              return (
                <WinnerTile
                  key={key}
                  amountMaxFont={geometry.amountMaxFont}
                  amountMaxWidth={geometry.amountMaxWidth}
                  tile={tile}
                  onSelect={onSelect}
                />
              );
          }
        }),
      )}
    </div>
  );
});
